/**
 * ListViewSchema Registry
 * 
 * Central registry for all ListViewSchemas.
 * This is the single entry point for getting a doctype's list schema.
 * 
 * Rules of Engagement:
 * - All list pages must obtain their schema via getListViewSchema(doctype)
 * - No component should hardcode list columns/filters/sort - use the schema
 * - Missing schemas throw in dev (loud failure), fallback in prod (safe)
 * 
 * To add a new doctype:
 * 1. Create listViewSchemas/{doctype}List.ts (copy from itemList.ts)
 * 2. Import and add to `schemas` map below
 */

import type { ListViewSchema, ListViewVariantConfig } from "./types";
import { itemListViewSchema } from "./itemList";
import { itemPriceListViewSchema } from "./itemPriceList";
import { pricingRuleListViewSchema } from "./pricingRuleList";
import { salesOrderListViewSchema } from "./salesOrderList";
import { stockEntryListViewSchema } from "./stockEntryList";
import { bookingListViewSchema } from "./bookingList";
import { bookingResourceConfigListViewSchema } from "./bookingResourceConfigList";

// Re-export types for external use
export type {
  ListViewSchema,
  ListViewVariantConfig,
  ListViewColumnConfig,
  ListViewSortConfig,
  ListViewFilter,
  ListViewFilterOp,
  ListViewQuickFilterConfig,
  ListViewActionConfig,
  ListViewColumnType,
  ListViewColumnAlign,
  ListViewMode,
} from "./types";

/**
 * Schema registry: DocType name → ListViewSchema
 * 
 * Add new doctypes here as they are created.
 * Keys must match the exact Frappe DocType name.
 */
const schemas: Record<string, ListViewSchema> = {
  Item: itemListViewSchema,
  "Item Price": itemPriceListViewSchema,
  "Pricing Rule": pricingRuleListViewSchema,
  "Sales Order": salesOrderListViewSchema,
  "Stock Entry": stockEntryListViewSchema,
  Booking: bookingListViewSchema,
  "Booking Resource Config": bookingResourceConfigListViewSchema,
};

/**
 * Create a safe fallback schema for production use.
 * This renders a minimal "all fields" layout when no schema is defined.
 */
function createFallbackSchema(doctype: string): ListViewSchema {
  const fallbackVariant: ListViewVariantConfig = {
    columns: [],
  };

  return {
    doctype,
    views: {
      experience: fallbackVariant,
      system: fallbackVariant,
    },
  };
}

/**
 * Get ListViewSchema for a doctype.
 * 
 * This is the canonical entry point for obtaining a doctype's list schema.
 * 
 * @param doctype - DocType name (e.g., "Item", "Sales Order")
 * @returns ListViewSchema for the doctype
 * 
 * @throws Error in development if schema is not registered
 * @example
 * const schema = getListViewSchema("Item");
 * const experienceConfig = schema.views.experience;
 */
export function getListViewSchema(doctype: string): ListViewSchema {
  const schema = schemas[doctype];

  if (!schema) {
    const message =
      `[ListViewSchema] No list schema registered for doctype "${doctype}". ` +
      `Add one in src/lib/views/listViewSchemas/${doctype
        .toLowerCase()
        .replace(/\s+/g, "")}List.ts and export it via index.ts.`;

    // Loud failure in development - forces devs/AI to add missing schema
    if (import.meta.env.DEV) {
      throw new Error(message);
    }

    // Safe fallback in production - log error but don't crash
    console.error(message);
    return createFallbackSchema(doctype);
  }

  return schema;
}

/**
 * Check if a doctype has a registered schema.
 * Useful for conditional rendering logic.
 */
export function hasListViewSchema(doctype: string): boolean {
  return doctype in schemas;
}

/**
 * Get list of all registered doctypes.
 * Useful for debugging and admin tools.
 */
export function getRegisteredListDoctypes(): string[] {
  return Object.keys(schemas);
}

